import type { ProductReference } from "@/src/domain/model/Product";

export enum Availability {
  // For products coming soon (DN do not matter)
  COMING_SOON_LISTED = 999,
  COMING_SOON_UNLISTED = -999,

  // For products available
  HIGH = 80,
  LOW = 1,
  MEDIUM = 40,
  ZERO = 0,
}

export const sortedAvailabilities = [
  Availability.COMING_SOON_LISTED,
  Availability.HIGH,
  Availability.MEDIUM,
  Availability.LOW,
  Availability.ZERO,
  Availability.COMING_SOON_UNLISTED,
];

export interface Availabilities<T> {
  availabilities: Record<Availability, T[]>;
  count: number;
}

export function emptyAvailabilities<T>(): Record<Availability, T[]> {
  return {
    [Availability.COMING_SOON_LISTED]: [],
    [Availability.COMING_SOON_UNLISTED]: [],
    [Availability.HIGH]: [],
    [Availability.LOW]: [],
    [Availability.MEDIUM]: [],
    [Availability.ZERO]: [],
  };
}

export function merchantAvailabilities<M>(
  product: ProductReference,
  merchants: M[],
  dn: (merchant: M) => number | undefined,
): Availabilities<M> {
  const availabilities = merchants.reduce((a, merchant) => {
    a[productAvailability(product, dn(merchant))].push(merchant);
    return a;
  }, emptyAvailabilities<M>());
  return {
    availabilities,
    count: countAvailabilities(availabilities),
  };
}

export function productAvailabilities(
  products: ProductReference[],
  dn: (product: ProductReference) => number | undefined,
): Availabilities<ProductReference> {
  const availabilities = products.reduce((a, product) => {
    a[productAvailability(product, dn(product))].push(product);
    return a;
  }, emptyAvailabilities<ProductReference>());
  return {
    availabilities,
    count: countAvailabilities(availabilities),
  };
}

function countAvailabilities<T>(
  availabilities: Record<Availability, T[]>,
): number {
  return Object.entries(availabilities)
    .filter(([availability]) => Number(availability) > 0)
    .reduce((count, [, items]) => count + items.length, 0);
}

function productAvailability(product: ProductReference, dn = 0): Availability {
  if (product.comingSoon) {
    return dn > 0
      ? Availability.COMING_SOON_LISTED
      : Availability.COMING_SOON_UNLISTED;
  }
  return dn > 0
    ? ([
        Availability.HIGH,
        Availability.MEDIUM,
        Availability.LOW,
        Availability.ZERO,
      ].find((threshold: number) => dn >= threshold) ?? Availability.ZERO)
    : Availability.ZERO;
}
